import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TreeStructure, Eye, Lightning, Warning } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BestThirdsTiebreakCard from "@/components/tournament/BestThirdsTiebreakCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Probable Knockout Bracket panel — visible on the Bracket tab when a
 * groups_knockout tournament hasn't generated its knockout yet.
 *
 * Loads `GET /api/tournaments/{id}/probable-bracket`, which returns a
 * read-only preview of the seeded bracket using current group standings
 * (tentative if the group stage is still in progress).
 *
 * Admins additionally see a "Generate Knockout from Groups" button which
 * persists the bracket — refuses when the group stage isn't complete unless
 * `force=true` (admin can confirm via an explicit prompt).
 */
export default function ProbableBracketCard({ tournamentId, isAdmin, onGenerated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/tournaments/${tournamentId}/probable-bracket`);
      setData(r.data);
    } catch (err) {
      // 400 means no groups configured — silently leave data null
      if (err.response?.status !== 400) {
        toast.error(err.response?.data?.detail || "Failed to load probable bracket");
      }
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/tournaments/${tournamentId}/generate-knockout-from-groups`,
        { force },
        { withCredentials: true },
      );
      toast.success(r.data.message || "Knockout bracket created");
      onGenerated?.();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (err.response?.status === 400 && /not complete/i.test(detail) && !force) {
        if (window.confirm(
          "Group stage isn't fully complete yet. Lock the bracket using current standings anyway?\n\n" +
          "(Best-3rd ties may shift if remaining group matches are played later.)"
        )) {
          return handleGenerate(true);
        }
        toast.warning("Bracket generation cancelled");
      } else {
        toast.error(detail || "Failed to generate bracket");
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground italic py-4" data-testid="probable-bracket-loading">
        Loading probable bracket…
      </div>
    );
  }
  if (!data || !data.bracket?.length) {
    return null;
  }

  const tentative = data.tentative;
  const slots = data.bracket;
  const pool = data.qualifier_pool || {};

  // Group slots into adjacent pairs (first-round matches)
  const pairs = [];
  for (let i = 0; i < slots.length; i += 2) {
    pairs.push([slots[i], slots[i + 1]]);
  }

  return (
    <Card className="bg-card border-secondary/30" data-testid="probable-bracket-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TreeStructure className="w-5 h-5 text-secondary" weight="fill" />
            <h3 className="text-base font-semibold">
              {tentative ? "Probable Knockout Bracket" : "Knockout Bracket Preview"}
            </h3>
            {tentative && (
              <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500" data-testid="probable-bracket-tentative-badge">
                <Warning className="w-3 h-3 mr-1" weight="fill" />
                Tentative
              </Badge>
            )}
          </div>
          {isAdmin && (
            <Button
              size="sm"
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
              data-testid="generate-knockout-from-groups-btn"
            >
              <Lightning className="w-4 h-4 mr-1.5" weight="fill" />
              {generating ? "Generating…" : "Generate Knockout from Groups"}
            </Button>
          )}
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p>
            {data.num_groups} groups · top {data.qualifiers_per_group} qualify
            {pool.best_thirds_used ? <> · <span className="text-foreground">{pool.best_thirds_used}</span> best Nth-place fillers</> : null}
            {pool.bye_count ? <> · <span className="text-foreground">{pool.bye_count}</span> BYE{pool.bye_count === 1 ? "" : "s"}</> : null}
          </p>
          {tentative && (
            <p className="italic">
              Based on current group standings — will update as group matches complete.
            </p>
          )}
        </div>

        {pool.tie_info && isAdmin && (
          <BestThirdsTiebreakCard
            tournamentId={tournamentId}
            tieInfo={pool.tie_info}
            onResolved={load}
          />
        )}


        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="probable-bracket-pairs">
          {pairs.map((pair, idx) => (
            <div
              key={idx}
              className="rounded-md border border-border bg-background/40 p-2 space-y-1"
              data-testid={`probable-bracket-pair-${idx}`}
            >
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">QF {idx + 1}</p>
              {pair.map((slot, si) => (
                <div key={si} className="flex items-center justify-between text-xs">
                  <Badge variant="outline" className="text-[10px] w-14 justify-center border-secondary/30 text-secondary">
                    {slot.slot_label}
                  </Badge>
                  <span className={`flex-1 ml-2 truncate ${slot.player_id ? "" : "italic text-muted-foreground"}`}>
                    {slot.player_name || "TBD"}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Current group standings
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {(data.standings || []).map((g) => (
              <div key={g.group_name} className="rounded-md border border-border bg-background/40 p-2">
                <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-1">Group {g.group_name}</p>
                {g.standings.slice(0, 4).map((r) => (
                  <div key={r.player_id} className="flex items-center justify-between text-[11px]">
                    <span className="tabular-nums w-4 text-muted-foreground">{r.position}.</span>
                    <span className="flex-1 truncate ml-1">{r.name || r.player_id}</span>
                    <span className="tabular-nums text-muted-foreground ml-2">
                      {r.wins}-{r.losses}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
